import { Link } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../api/axios';
import AppShell from '../components/AppShell';
import ImportInspectionsModal from '../components/ImportInspectionsModal';
import { useAuth } from '../context/AuthContext';

const EMPTY_STATS = {
  today: { total: 0, completed: 0, in_progress: 0 },
  overall: { total: 0, completed: 0, in_progress: 0, owners: 0, vehicles: 0 },
  services_provided_today: 0,
  services_provided_total: 0,
  services: [],
  balance: {
    amount: null,
    currency: 'SAR',
    available: false,
    label: 'غير متاح',
    note: 'لا توجد بيانات مالية مسجلة حاليًا.',
  },
};

const EMPTY_FILTERS = { search: '', status: '', manufacturerId: '', modelId: '', dateFrom: '', dateTo: '' };

const EMPTY_META = {
  total: 0,
  page: 1,
  per_page: 10,
  last_page: 1,
  from: 0,
  to: 0,
};

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

const STATUS_OPTIONS = [
  { value: '', label: 'كل الحالات' },
  { value: 'pending', label: 'بانتظار البدء' },
  { value: 'in_progress', label: 'قيد التنفيذ' },
  { value: 'completed', label: 'مكتمل' },
];

function normalizeList(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object') return Object.values(payload);
  return [];
}

function normalizeDashboardStats(payload) {
  const source = payload?.data && typeof payload.data === 'object' ? payload.data : payload;

  return {
    today: {
      total: Number(source?.today?.total ?? 0),
      completed: Number(source?.today?.completed ?? 0),
      in_progress: Number(source?.today?.in_progress ?? 0),
    },
    overall: {
      total: Number(source?.overall?.total ?? 0),
      completed: Number(source?.overall?.completed ?? 0),
      in_progress: Number(source?.overall?.in_progress ?? 0),
      owners: Number(source?.overall?.owners ?? 0),
      vehicles: Number(source?.overall?.vehicles ?? 0),
    },
    services_provided_today: Number(source?.services_provided_today ?? 0),
    services_provided_total: Number(source?.services_provided_total ?? 0),
    services: Array.isArray(source?.services) ? source.services : [],
    balance: {
      amount: source?.balance?.amount ?? null,
      currency: source?.balance?.currency || 'SAR',
      available: Boolean(source?.balance?.available),
      label: source?.balance?.label || 'غير متاح',
      note: source?.balance?.note || 'لا توجد بيانات مالية مسجلة حاليًا.',
    },
  };
}

function normalizeMeta(meta, fallbackPerPage) {
  const perPage = Number(meta?.per_page) || fallbackPerPage || EMPTY_META.per_page;
  return {
    total: Number(meta?.total ?? 0),
    page: Math.max(1, Number(meta?.page) || 1),
    per_page: perPage,
    last_page: Math.max(1, Number(meta?.last_page) || 1),
    from: Number(meta?.from ?? 0),
    to: Number(meta?.to ?? 0),
  };
}

function formatStatus(status) {
  const map = {
    pending: 'بانتظار البدء',
    in_progress: 'قيد التنفيذ',
    completed: 'مكتمل',
  };

  return map[status] || status || '-';
}

function statusBadgeClass(status) {
  if (status === 'completed') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (status === 'in_progress') return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
}

// نافذة أرقام الصفحات حول الصفحة الحالية (بحد أقصى 5 أرقام).
function buildPageWindow(current, lastPage) {
  const size = Math.min(5, lastPage);
  let start = Math.max(1, current - Math.floor(size / 2));
  if (start + size - 1 > lastPage) start = lastPage - size + 1;
  return Array.from({ length: size }, (_, index) => start + index);
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(EMPTY_STATS);
  const [deletingId, setDeletingId] = useState(null);

  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [perPage, setPerPage] = useState(EMPTY_META.per_page);
  const [page, setPage] = useState(1);
  const [manufacturers, setManufacturers] = useState([]);
  const [models, setModels] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [meta, setMeta] = useState(EMPTY_META);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const requestIdRef = useRef(0);

  const loadStats = useCallback(() => {
    api.get('/dashboard/stats')
      .then((res) => setStats(normalizeDashboardStats(res.data)))
      .catch(() => setStats(EMPTY_STATS));
  }, []);

  const filterParams = useMemo(() => {
    const params = {};
    if (filters.search) params.search = filters.search;
    if (filters.status) params.status = filters.status;
    if (filters.manufacturerId) params.manufacturer_id = filters.manufacturerId;
    if (filters.modelId) params.model_id = filters.modelId;
    if (filters.dateFrom) params.date_from = filters.dateFrom;
    if (filters.dateTo) params.date_to = filters.dateTo;
    return params;
  }, [filters]);

  const loadInspections = useCallback(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    const params = { ...filterParams, page, per_page: perPage };

    setListLoading(true);
    setListError('');

    api.get('/inspections', { params })
      .then((res) => {
        if (requestIdRef.current !== requestId) return;
        const rows = normalizeList(res.data?.data);
        const nextMeta = normalizeMeta(res.data?.meta, perPage);
        setInspections(rows);
        setMeta(nextMeta);
        // الخادم يضبط رقم الصفحة إذا تجاوز آخر صفحة متاحة.
        if (nextMeta.page !== page) setPage(nextMeta.page);
      })
      .catch((error) => {
        if (requestIdRef.current !== requestId) return;
        setInspections([]);
        setMeta({ ...EMPTY_META, per_page: perPage });
        setListError(
          error?.response?.data?.message
          || error?.userMessage
          || 'تعذر تحميل قائمة الفحوصات. حاول مرة أخرى.',
        );
      })
      .finally(() => {
        if (requestIdRef.current !== requestId) return;
        setListLoading(false);
      });
  }, [page, perPage, filterParams]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    let mounted = true;

    Promise.all([api.get('/manufacturers'), api.get('/models')])
      .then(([manufacturersRes, modelsRes]) => {
        if (!mounted) return;
        setManufacturers(normalizeList(manufacturersRes.data));
        setModels(normalizeList(modelsRes.data));
      })
      .catch(() => {
        if (!mounted) return;
        setManufacturers([]);
        setModels([]);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    loadInspections();
  }, [loadInspections]);

  // تأخير بسيط قبل إرسال البحث حتى لا يُستدعى الخادم مع كل حرف.
  useEffect(() => {
    const timer = setTimeout(() => {
      const value = searchInput.trim();
      setFilters((prev) => (prev.search === value ? prev : { ...prev, search: value }));
    }, 400);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const exportExcel = async () => {
    setExporting(true);
    setListError('');

    try {
      const response = await api.get('/inspections/export', {
        params: filterParams,
        responseType: 'blob',
        timeout: 60000,
      });

      const disposition = String(response.headers?.['content-disposition'] || '');
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const filename = match?.[1] || `inspections-${new Date().toISOString().slice(0, 10)}.xlsx`;

      const url = window.URL.createObjectURL(new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();

      // لا نلغي الرابط فورًا حتى لا ينتج ملف تالف في بعض المتصفحات.
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      // رسالة الخطأ تصل كـ Blob لأن نوع الاستجابة blob.
      let message = 'تعذّر تصدير ملف الإكسل. حاول مرة أخرى.';
      const payload = error?.response?.data;

      if (payload instanceof Blob) {
        const text = await payload.text().catch(() => '');
        try {
          const parsed = JSON.parse(text);
          if (parsed?.message) message = parsed.message;
        } catch {
          // نبقي الرسالة الافتراضية إذا لم يكن الرد JSON.
        }
      }

      setListError(message);
    } finally {
      setExporting(false);
    }
  };

  const updateFilter = (key, value) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setPage(1);
    setSearchInput('');
    setFilters(EMPTY_FILTERS);
  };

  const hasActiveFilters = Boolean(
    filters.search
    || filters.status
    || filters.manufacturerId
    || filters.modelId
    || filters.dateFrom
    || filters.dateTo,
  );

  const deleteInspection = async (inspection) => {
    if (deletingId) return;

    const number = inspection.display_number || inspection.branch_serial || inspection.id;
    if (!window.confirm(`هل تريد حذف الفحص #${number} كاملًا مع بياناته؟`)) return;

    setDeletingId(inspection.id);
    try {
      await api.delete(`/inspections/${inspection.id}`);
      loadStats();
      loadInspections();
    } catch {
      window.alert('تعذر حذف الفحص الآن. حاول مرة أخرى.');
    } finally {
      setDeletingId(null);
    }
  };

  const isViewer = user?.role === 'viewer';
  const pageWindow = useMemo(() => buildPageWindow(meta.page, meta.last_page), [meta.page, meta.last_page]);

  const cards = [
    { title: 'إجمالي فحوصات اليوم', value: stats.today.total, subtitle: 'الفحوصات المنشأة اليوم' },
    { title: 'المكتملة اليوم', value: stats.today.completed, subtitle: 'المقفلة بنجاح' },
    { title: 'قيد التنفيذ اليوم', value: stats.today.in_progress, subtitle: 'ما زالت تعمل' },
    { title: 'إجمالي الفحوصات', value: stats.overall.total, subtitle: 'منذ بداية النظام' },
    { title: 'الخدمات المقدمة اليوم', value: stats.services_provided_today, subtitle: 'إجمالي الأقسام المنفذة اليوم' },
    {
      title: 'الرصيد',
      value: stats.balance.available ? `${stats.balance.amount} ${stats.balance.currency}` : stats.balance.label,
      subtitle: stats.balance.note,
      muted: !stats.balance.available,
    },
  ];

  return (
    <AppShell title="لوحة التحكم">
      {!isViewer && (
        <div className="mb-8 flex flex-wrap items-center justify-center gap-4">
          <Link
            to="/inspection/new"
            className="rounded-2xl bg-primary px-10 py-5 text-white text-2xl font-bold shadow-lg hover:opacity-90"
          >
            ▶ ابدأ الفحص
          </Link>
          <button
            type="button"
            onClick={() => setImportOpen(true)}
            className="rounded-2xl bg-emerald-600 px-10 py-5 text-2xl font-bold text-white shadow-lg hover:bg-emerald-700"
          >
            ⬆ استيراد فحوصات
          </button>
        </div>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
        {cards.map((c) => (
          <div key={c.title} className="bg-white border rounded-2xl p-5 shadow-sm">
            <p className="text-sm text-gray-500">{c.title}</p>
            <p className={`text-3xl font-extrabold mt-2 ${c.muted ? 'text-slate-500' : 'text-primary'}`}>{c.value}</p>
            {c.subtitle && <p className="mt-2 text-xs leading-6 text-slate-500">{c.subtitle}</p>}
          </div>
        ))}
      </div>

      <div className="grid xl:grid-cols-[1.3fr_0.9fr] gap-4 mb-6">
        <section className="bg-white border rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="text-lg font-bold text-slate-900">ملخص النشاط</h2>
              <p className="text-sm text-slate-500">نظرة سريعة على النظام والعملاء والمركبات.</p>
            </div>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600">
              الخدمات الكلية: {stats.services_provided_total}
            </span>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs text-slate-500">الملاك</p>
              <p className="mt-2 text-2xl font-bold text-slate-900">{stats.overall.owners}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs text-slate-500">المركبات</p>
              <p className="mt-2 text-2xl font-bold text-slate-900">{stats.overall.vehicles}</p>
            </div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <p className="text-xs text-emerald-700">الفحوصات المكتملة</p>
              <p className="mt-2 text-2xl font-bold text-emerald-800">{stats.overall.completed}</p>
            </div>
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-xs text-amber-700">الفحوصات قيد التنفيذ</p>
              <p className="mt-2 text-2xl font-bold text-amber-800">{stats.overall.in_progress}</p>
            </div>
          </div>
        </section>

        <section className="bg-white border rounded-2xl p-5 shadow-sm">
          <h2 className="text-lg font-bold text-slate-900 mb-1">الخدمات المقدمة</h2>
          <p className="text-sm text-slate-500 mb-4">عدد مرات تنفيذ كل نوع فحص اليوم وإجمالًا.</p>

          <div className="space-y-3">
            {stats.services.length ? stats.services.map((service) => (
              <div key={service.key} className="rounded-xl border border-slate-200 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="font-semibold text-slate-900">{service.icon ? `${service.icon} ` : ''}{service.name}</div>
                    {service.name_en && <div className="text-xs text-slate-500">{service.name_en}</div>}
                  </div>
                  <div className="text-left">
                    <div className="text-sm font-bold text-primary">اليوم: {service.today_count}</div>
                    <div className="text-xs text-slate-500">الإجمالي: {service.total_count}</div>
                  </div>
                </div>
              </div>
            )) : (
              <div className="rounded-xl border border-dashed border-slate-300 p-4 text-sm text-slate-500">
                لا توجد خدمات مسجلة بعد.
              </div>
            )}
          </div>
        </section>
      </div>

      <div className="bg-white rounded-2xl border shadow-sm">
        <div className="border-b p-5">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="text-lg font-bold text-slate-900">قائمة الفحوصات</h2>
              <p className="text-sm text-slate-500">ابحث وصفِّ النتائج ثم تنقّل بين الصفحات.</p>
            </div>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600">
              إجمالي النتائج: {meta.total}
            </span>
          </div>

          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <div className="xl:col-span-2">
              <label className="mb-1 block text-xs font-semibold text-slate-600" htmlFor="inspections-search">
                بحث
              </label>
              <input
                id="inspections-search"
                type="search"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="رقم الفحص أو اسم المالك أو رقم الجوال أو رقم الهيكل (VIN)"
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600" htmlFor="inspections-manufacturer">
                نوع السيارة
              </label>
              <select
                id="inspections-manufacturer"
                value={filters.manufacturerId}
                onChange={(event) => updateFilter('manufacturerId', event.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary"
              >
                <option value="">كل الأنواع</option>
                {manufacturers.map((item) => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600" htmlFor="inspections-model">
                موديل السيارة
              </label>
              <select
                id="inspections-model"
                value={filters.modelId}
                onChange={(event) => updateFilter('modelId', event.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary"
              >
                <option value="">كل الموديلات</option>
                {models.map((item) => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600" htmlFor="inspections-status">
                الحالة
              </label>
              <select
                id="inspections-status"
                value={filters.status}
                onChange={(event) => updateFilter('status', event.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary"
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600" htmlFor="inspections-per-page">
                عدد الصفوف
              </label>
              <select
                id="inspections-per-page"
                value={perPage}
                onChange={(event) => {
                  setPage(1);
                  setPerPage(Number(event.target.value));
                }}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary"
              >
                {PER_PAGE_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option} لكل صفحة</option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600" htmlFor="inspections-date-from">
                من تاريخ
              </label>
              <input
                id="inspections-date-from"
                type="date"
                value={filters.dateFrom}
                max={filters.dateTo || undefined}
                onChange={(event) => updateFilter('dateFrom', event.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600" htmlFor="inspections-date-to">
                إلى تاريخ
              </label>
              <input
                id="inspections-date-to"
                type="date"
                value={filters.dateTo}
                min={filters.dateFrom || undefined}
                onChange={(event) => updateFilter('dateTo', event.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary"
              />
            </div>

            <div className="flex items-end gap-2 xl:col-span-2">
              <button
                type="button"
                onClick={resetFilters}
                disabled={!hasActiveFilters}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                مسح الفلاتر
              </button>
              <button
                type="button"
                onClick={loadInspections}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                تحديث
              </button>
              <button
                type="button"
                onClick={exportExcel}
                disabled={exporting || !meta.total}
                title="يصدّر كل النتائج المطابقة للبحث والفلاتر، لا الصفحة الحالية فقط"
                className="rounded-xl border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {exporting ? 'جاري التصدير...' : 'تصدير إكسل'}
              </button>
              {listLoading && <span className="text-xs text-slate-500">جاري التحميل...</span>}
            </div>
          </div>
        </div>

        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr>
                <th className="p-3 text-right">رقم الفحص</th>
                <th className="p-3 text-right">المزاد</th>
                <th className="p-3 text-right">المالك</th>
                <th className="p-3 text-right">الشركة</th>
                <th className="p-3 text-right">الموديل</th>
                <th className="p-3 text-right">الحالة</th>
                <th className="p-3 text-right">التاريخ</th>
                <th className="p-3 text-right">الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {inspections.map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="p-3">#{r.display_number || r.branch_serial || r.id}</td>
                  <td className="p-3">{r.auction || r.branch || '-'}</td>
                  <td className="p-3">{r.owner_name || '-'}</td>
                  <td className="p-3">{r.manufacturer}</td>
                  <td className="p-3">{r.model}</td>
                  <td className="p-3">
                    <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${statusBadgeClass(r.status)}`}>
                      {formatStatus(r.status)}
                    </span>
                  </td>
                  <td className="p-3 whitespace-nowrap">{r.created_at}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-2">
                      {isViewer ? (
                        r.status === 'completed' && (
                          <Link
                            to={`/inspection/${r.id}/report`}
                            className="inline-flex items-center rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
                          >
                            عرض التقرير
                          </Link>
                        )
                      ) : (
                        <>
                          <Link
                            to={`/inspection/${r.id}/menu`}
                            className="inline-flex items-center rounded-lg bg-primary px-3 py-1.5 text-white text-xs font-semibold hover:opacity-90"
                          >
                            متابعة الفحص
                          </Link>
                          {r.status === 'completed' && (
                            <Link
                              to={`/inspection/${r.id}/report`}
                              className="inline-flex items-center rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
                            >
                              عرض التقرير
                            </Link>
                          )}
                          <Link
                            to={`/inspection/${r.id}/edit`}
                            className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            تعديل
                          </Link>
                          <button
                            type="button"
                            onClick={() => deleteInspection(r)}
                            disabled={deletingId === r.id}
                            className="inline-flex items-center rounded-lg border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100 disabled:opacity-60"
                          >
                            {deletingId === r.id ? 'جاري الحذف...' : 'حذف'}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!inspections.length && (
                <tr>
                  <td colSpan="8" className="p-6 text-center text-slate-500">
                    {listError || (listLoading
                      ? 'جاري تحميل الفحوصات...'
                      : (hasActiveFilters
                        ? 'لا توجد فحوصات مطابقة لمعايير البحث الحالية.'
                        : 'لا توجد عمليات فحص حديثة حتى الآن.'))}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {meta.total > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t p-4">
            <p className="text-xs text-slate-500">
              عرض {meta.from} - {meta.to} من أصل {meta.total} فحص (صفحة {meta.page} من {meta.last_page})
            </p>

            <div className="flex flex-wrap items-center gap-1">
              <button
                type="button"
                onClick={() => setPage(1)}
                disabled={meta.page <= 1 || listLoading}
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
              >
                الأولى
              </button>
              <button
                type="button"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={meta.page <= 1 || listLoading}
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
              >
                السابق
              </button>

              {pageWindow.map((pageNumber) => (
                <button
                  key={pageNumber}
                  type="button"
                  onClick={() => setPage(pageNumber)}
                  disabled={listLoading}
                  className={`min-w-[2.25rem] rounded-lg border px-3 py-1.5 text-xs font-semibold disabled:opacity-60 ${
                    pageNumber === meta.page
                      ? 'border-primary bg-primary text-white'
                      : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {pageNumber}
                </button>
              ))}

              <button
                type="button"
                onClick={() => setPage((prev) => Math.min(meta.last_page, prev + 1))}
                disabled={meta.page >= meta.last_page || listLoading}
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
              >
                التالي
              </button>
              <button
                type="button"
                onClick={() => setPage(meta.last_page)}
                disabled={meta.page >= meta.last_page || listLoading}
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
              >
                الأخيرة
              </button>
            </div>
          </div>
        )}
      </div>
      <ImportInspectionsModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => {
          setPage(1);
          loadInspections();
          loadStats();
        }}
      />
    </AppShell>
  );
}
